import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Printer, Link2, Award, Info } from 'lucide-react';
import { EmptyState, PageSkeleton } from '@/components/dashboard/MetricCard';
import Diploma from '@/components/academy/Diploma';
import { academyApi } from '@/services/academyApi';
import { courseHref } from '@/pages/dashboard/DashboardAcademia';
import { useSiteLang } from '@/components/site/SiteLang';

// Textos de la interfaz alrededor del diploma. El diploma (components/academy/Diploma.jsx) es un documento en español
// y no se traduce; en inglés se avisa con una nota discreta.
const T = {
  es: {
    notFound: 'No hemos encontrado este diploma',
    back: 'Volver a la Academia',
    copied: 'Enlace de verificación copiado',
    copyLink: 'Copiar enlace de verificación',
    download: 'Descargar en PDF',
    revoked: 'Este diploma está anulado.',
    help: 'Para guardarlo en PDF pulsa «Descargar en PDF» y, en la ventana de impresión, elige «Guardar como PDF», orientación horizontal y activa «Gráficos de fondo».',
    nameFromProfile: ' El nombre sale de tu perfil porque aún no has completado la verificación de identidad; si no es tu nombre completo, escríbenos y lo corregimos.',
    notice: null,
  },
  en: {
    notFound: 'We couldn’t find this certificate',
    back: 'Back to the Academy',
    copied: 'Verification link copied',
    copyLink: 'Copy verification link',
    download: 'Download as PDF',
    revoked: 'This certificate has been revoked.',
    help: 'To save it as a PDF, click ‘Download as PDF’, then in the print dialogue choose ‘Save as PDF’, landscape orientation and turn on ‘Background graphics’.',
    nameFromProfile: ' The name comes from your profile because you haven’t completed identity verification yet; if it isn’t your full name, get in touch and we’ll correct it.',
    notice: 'The certificate is issued in Spanish.',
  },
};

/* Diploma de un curso de FHC Academy (/dashboard/academia/diploma/:code). Se descarga con «Imprimir → Guardar como PDF». */
export default function DashboardDiploma() {
  const { code } = useParams();
  const { lang } = useSiteLang();
  const t = T[lang] || T.es;
  const [cert, setCert] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    academyApi.certificate(code).then(setCert).catch((e) => setError(e.message));
  }, [code]);

  if (error) {
    return <EmptyState icon={Award} title={t.notFound} text={error}
      action={<Link to="/dashboard/academia" className="rounded-xl bg-[#c9a84c] px-4 py-2 text-sm font-semibold text-[#0a1628]">{t.back}</Link>} />;
  }
  if (!cert) return <PageSkeleton cards={0} rows={4} />;

  const verifyUrl = `${window.location.origin}/diploma/${cert.code}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(verifyUrl); toast.success(t.copied); } catch { toast(verifyUrl); }
  };

  return (
    <div className="space-y-5" data-testid="dashboard-diploma">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <Link to={courseHref(cert.course_id)} className="inline-flex w-fit items-center gap-1.5 text-sm text-gray-400 hover:text-white"><ArrowLeft size={15} /> {t.back}</Link>
        {!cert.revoked && (
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={copy} className="inline-flex items-center gap-2 rounded-xl border border-white/10 px-4 py-2.5 text-sm text-white hover:bg-white/[0.05]">
            <Link2 size={15} /> {t.copyLink}
          </button>
          <button type="button" onClick={() => window.print()} data-testid="diploma-print"
            className="inline-flex items-center gap-2 rounded-xl bg-[#c9a84c] px-4 py-2.5 text-sm font-semibold text-[#0a1628] hover:bg-[#d6b75a]">
            <Printer size={15} /> {t.download}
          </button>
        </div>
        )}
      </div>
      {cert.revoked && <p className="rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">{t.revoked}</p>}
      {t.notice && <p className="text-xs text-gray-500">{t.notice}</p>}
      <Diploma cert={cert} verifyUrl={verifyUrl.replace(/^https?:\/\//, '')} />
      {!cert.revoked && (
      <p className="flex items-start gap-2 text-xs text-gray-400 leading-relaxed">
        <Info size={14} className="mt-0.5 flex-shrink-0 text-[#c9a84c]" />
        <span>
          {t.help}
          {cert.name_source === 'perfil' && t.nameFromProfile}
        </span>
      </p>
      )}
    </div>
  );
}
